/**
 * Reverse Geocode Utilities
 *
 * Uses the Google Maps JavaScript API Geocoder (already loaded client-side)
 * to convert lat/lng into address fields for auto-filling asset forms.
 *
 * Cost: FREE — client-side geocoding is included in the Maps JS API.
 */

/**
 * Helper to extract a specific address component from the geocoder results.
 *
 * @param {Array} components - address_components from the geocoder response
 * @param {string|string[]} types - the type(s) to look for (e.g., "route", ["neighborhood", "sublocality"])
 * @returns {string|null} The long_name of the first matching component, or null
 */
function getAddressComponent(components, types) {
    const typeArray = Array.isArray(types) ? types : [types];
    for (const component of components) {
        for (const type of typeArray) {
            if (component.types.includes(type)) {
                return component.long_name;
            }
        }
    }
    return null;
}

/**
 * Reverse-geocode a lat/lng coordinate and return parsed address fields
 * mapped to the asset form field names.
 *
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object|null>} Object with form field names as keys, or null on failure.
 *   Possible keys: StreetName, SubDivisionName, Address, Location
 */
export async function reverseGeocode(lat, lng) {
    // Ensure the Google Maps API is loaded
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        console.warn("[Geocode] Google Maps Geocoder not available");
        return null;
    }

    try {
        const geocoder = new window.google.maps.Geocoder();
        const response = await geocoder.geocode({ location: { lat, lng } });

        if (!response || !response.results || response.results.length === 0) {
            console.warn("[Geocode] No results for coordinates:", lat, lng);
            return null;
        }

        // Google Geocoder returns multiple results ordered by type (plus_code, street_address,
        // route, neighborhood, locality, etc.). The first result is often a Plus Code which
        // lacks street-level components. We scan ALL results to build the most complete field set.
        const fields = {};
        let route = null;
        let streetNumber = null;

        for (const result of response.results) {
            const components = result.address_components;
            if (!components || components.length === 0) continue;

            // StreetName ← "route" (e.g., "Main Street", "Oak Avenue")
            if (!route) {
                route = getAddressComponent(components, "route");
                if (route) {
                    fields.StreetName = route;
                }
            }

            // street_number for building the full Address
            if (!streetNumber) {
                streetNumber = getAddressComponent(components, "street_number");
            }

            // SubDivisionName ← "neighborhood" or "sublocality"
            if (!fields.SubDivisionName) {
                const subdivision = getAddressComponent(components, ["neighborhood", "sublocality", "sublocality_level_1"]);
                if (subdivision) {
                    fields.SubDivisionName = subdivision;
                }
            }

            // Location ← "locality" (e.g., "Stillwater", "Oklahoma City")
            if (!fields.Location) {
                const locality = getAddressComponent(components, "locality");
                if (locality) {
                    fields.Location = locality;
                }
            }

            // Stop early if we have everything
            if (route && fields.SubDivisionName && fields.Location && streetNumber) break;
        }

        // Address ← "street_number" + "route" concatenated (e.g., "123 Main Street")
        if (streetNumber && route) {
            fields.Address = `${streetNumber} ${route}`;
        } else if (route) {
            fields.Address = route;
        }

        // Return null if we couldn't extract any fields
        if (Object.keys(fields).length === 0) {
            return null;
        }

        return fields;
    } catch (err) {
        console.error("[Geocode] Reverse geocoding failed:", err);
        return null;
    }
}
